import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useSettings } from '../context/SettingsContext';
import { useBountyCount } from '../context/BountyCountContext';
import { getDisplayName } from '../utils/helpers';
import NotificationBell from './NotificationBell';
import './Navigation.css';

const Navigation = ({ onMenuClick }) => {
  const { user, logout, isAuthenticated } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { settings } = useSettings();
  const { totalCount } = useBountyCount();
  const navigate = useNavigate();
  const location = useLocation();
  const [showMenu, setShowMenu] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileRef = useRef(null);
  const profileCloseTimerRef = useRef(null);
  const displayName = getDisplayName(user);

  const channelFromUrl = new URLSearchParams(location.search).get('channel') || '';

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const openProfileMenu = () => {
    if (profileCloseTimerRef.current) clearTimeout(profileCloseTimerRef.current);
    setShowProfileMenu(true);
  };

  const scheduleCloseProfileMenu = () => {
    if (profileCloseTimerRef.current) clearTimeout(profileCloseTimerRef.current);
    profileCloseTimerRef.current = setTimeout(() => setShowProfileMenu(false), 180);
  };

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (profileCloseTimerRef.current) clearTimeout(profileCloseTimerRef.current);
    };
  }, []);

  // 路由變更時自動收合行動版選單與個人資料下拉，避免跳轉後選單仍展開遮擋畫面
  useEffect(() => {
    setShowMenu(false);
    setShowProfileMenu(false);
  }, [location.pathname, location.search]);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <nav className="navbar">
        <div className="navbar-container">
          <button className="sidebar-toggle" onClick={onMenuClick} title="開啟選單">
            ☰
          </button>
          <Link to="/" className="navbar-brand">
            {settings.header_title || 'Tech Forum'}
          </Link>

          <button
            className="navbar-toggle"
            onClick={() => setShowMenu(!showMenu)}
          >
            ☰
          </button>

          <div className={`navbar-menu ${showMenu ? 'active' : ''}`}>
            <div className="navbar-nav">
              <Link to="/" className={`nav-link ${isActive('/') ? 'nav-active' : ''}`}>
                首頁
              </Link>

              <button
                type="button"
                className="nav-cmdk-trigger"
                onClick={() => window.dispatchEvent(new Event('tech-forum:open-command-palette'))}
                title="快速指令面板（⌘K / Ctrl+K）"
              >
                <span>🔍</span>
                <span className="nav-cmdk-keys">⌘K</span>
              </button>

              {isAuthenticated ? (
                <>
                  <Link to="/bounties" className={`nav-link ${isActive('/bounties') ? 'nav-active' : ''}`}>
                    懸賞榜
                    {totalCount > 0 && <span className="nav-count-badge">{totalCount > 99 ? '99+' : totalCount}</span>}
                  </Link>
                  <Link to="/rewards" className={`nav-link ${isActive('/rewards') ? 'nav-active' : ''}`}>
                    兌換商城
                  </Link>
                  <Link to="/rankings" className={`nav-link ${isActive('/rankings') ? 'nav-active' : ''}`}>
                    排行榜
                  </Link>
                  <Link to="/users" className={`nav-link ${isActive('/users') ? 'nav-active' : ''}`}>
                    人員名錄
                  </Link>
                  {user?.role === 'ADMIN' && (
                    <Link to="/admin" className={`nav-link ${isActive('/admin') ? 'nav-active' : ''}`}>
                      管理後台
                    </Link>
                  )}
                  <NotificationBell />
                  <div
                    className="nav-profile-wrapper"
                    ref={profileRef}
                    onPointerEnter={(e) => { if (e.pointerType === 'mouse') openProfileMenu(); }}
                    onPointerLeave={(e) => { if (e.pointerType === 'mouse') scheduleCloseProfileMenu(); }}
                  >
                    {/* 觸控裝置只靠 onClick 切換，避免 hover/focus 在點擊瞬間先開再被 toggle 關掉造成要點兩下 */}
                    <button className="nav-profile-trigger" onClick={() => setShowProfileMenu(prev => !prev)}>
                      <span className="nav-avatar">{displayName?.charAt(0)?.toUpperCase()}</span>
                      <span className="nav-username">{displayName}</span>
                      <span className="nav-caret">▾</span>
                    </button>
                    {showProfileMenu && (
                      <div className="nav-profile-dropdown">
                        <Link to="/profile" className="dropdown-item" onClick={() => setShowProfileMenu(false)}>
                          👤 我的資料
                        </Link>
                        <Link to="/bookmarks" className="dropdown-item" onClick={() => setShowProfileMenu(false)}>
                          ★ 我的收藏
                        </Link>
                        <Link to="/subscriptions" className="dropdown-item" onClick={() => setShowProfileMenu(false)}>
                          🔖 我的訂閱
                        </Link>
                        <button className="dropdown-item" onClick={toggleTheme}>
                          {theme === 'light' ? '🌙 深色模式' : '☀️ 淺色模式'}
                        </button>
                        <div className="dropdown-divider" />
                        <button className="dropdown-item dropdown-logout" onClick={handleLogout}>
                          🚪 登出
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <Link to="/login" className="nav-link">
                    登入
                  </Link>
                  <Link to="/register" className="nav-link nav-btn-register">
                    註冊
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Floating Action Button for creating posts */}
      {isAuthenticated && (
        <Link
          to={`/posts/create${channelFromUrl ? `?channel=${channelFromUrl}` : ''}`}
          className="fab-create-post"
          title="發文"
        >
          <span className="fab-icon">✏️</span>
        </Link>
      )}
    </>
  );
};

export default Navigation;

